/*
  Admin → Packages → Discount.
  ------------------------------------------------------------------
  Sister page to "All packages". Lists every user who opted into the
  earn-money program (`enabled_earning` / `accept_terms_earn_money`)
  AND already owns at least one AffiliateCode (i.e. they have a
  discount code their referrals can use).

  For each user the admin can replace the set of *discounted*
  packages tied to their code. The mobile paywall reads this exact
  set when a buyer enters the code — see `getPackageByDiscountCode`
  in affiliate.controller.js — so editing here swaps which packages
  the referral sees in real time.

  UX:
    * One card per earner with their info + code + assigned packages
      grouped by platform.
    * "Manage packages" opens a dialog with the FULL catalog (from
      /payment/admin/packages) split into platform tabs; the admin
      ticks the packages that should be available via the code.
    * Save sends `{ packageIds: [...] }` to
      /affiliation/admin/discount-users/:userId/assignments. The
      server diffs and inserts/soft-deletes AffiliateCode rows.
*/

import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { openAppAlert } from "components/AppDialogs";

import {
  GetAdminPackagesCall,
  GetAdminDiscountUsersCall,
  AdminUpdateDiscountUserAssignmentsCall,
} from "../../../redux/api/package.api";

const PLATFORM_LABEL = {
  ios: "iOS",
  android: "Android",
  web: "Web",
  ios_web: "iOS + Web",
  android_web: "Android + Web",
  ios_android_web: "iOS + Android + Web",
};

function platformChipColor(type) {
  switch (type) {
    case "android":
      return "success";
    case "web":
      return "info";
    case "ios_web":
      return "primary";
    case "android_web":
      return "warning";
    case "ios_android_web":
      return "secondary";
    default:
      return "default";
  }
}

function initialsFor(user) {
  const first = user?.first_name?.charAt(0) || "";
  const last = user?.last_name?.charAt(0) || "";
  const combo = `${first}${last}`.toUpperCase();
  if (combo) return combo;
  if (user?.email) return user.email.charAt(0).toUpperCase();
  return "?";
}

function fullNameFor(user) {
  return (
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    "Unknown earner"
  );
}

/*
  Build a quick label for an AllPackages catalog row so the admin
  can recognise what they're ticking. Uses base-row fields directly
  (no translation lookup needed — admins are reading metadata).
*/
function packageLabel(pkg) {
  const name = pkg.name || pkg.product_id || pkg.id;
  const id = pkg.identifier || pkg.product_id || "";
  return id ? `${name} · ${id}` : name;
}

export default function DiscountUsersPage() {
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([GetAdminDiscountUsersCall(), GetAdminPackagesCall()]).then(
      ([usersRes, pkgsRes]) => {
        if (cancelled) return;
        setUsers(Array.isArray(usersRes?.users) ? usersRes.users : []);
        setCatalog(Array.isArray(pkgsRes?.packages) ? pkgsRes.packages : []);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const visibleUsers = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => {
      const haystack =
        `${u.user.first_name || ""} ${u.user.last_name || ""} ${u.user.email || ""} ${(u.codes || []).join(" ")}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [users, filter]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        component="main"
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "flex-end" }}
            justifyContent="space-between"
          >
            <Box>
              <MDTypography
                variant="h4"
                fontWeight="bold"
                color="text"
                sx={{ letterSpacing: "-0.02em" }}
              >
                Discount holders
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{ mt: 1, maxWidth: 820, lineHeight: 1.6, opacity: 0.85 }}
              >
                Every user who accepted the earn-money program and owns an
                affiliate / discount code. Pick a user to change which
                packages their code swaps into the paywall — for example,
                remove the default 10%-off bundle and assign the 30%-off
                package instead.
              </MDTypography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {loading
                  ? "Loading…"
                  : `${visibleUsers.length} of ${users.length} ${
                      users.length === 1 ? "earner" : "earners"
                    } shown`}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Search by name, email or code"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280 }}
              />
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => setRefreshTick((t) => t + 1)}
                  size="small"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress size={36} />
            </Box>
          ) : visibleUsers.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                py: 8,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No discount holders match this search.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              {visibleUsers.map((entry) => (
                <DiscountUserCard
                  key={entry.user.id}
                  entry={entry}
                  onEdit={() => setEditing(entry)}
                />
              ))}
            </Stack>
          )}
        </Stack>

        {editing ? (
          <ManageAssignmentsDialog
            entry={editing}
            catalog={catalog}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              setRefreshTick((t) => t + 1);
            }}
          />
        ) : null}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

function DiscountUserCard({ entry, onEdit }) {
  const { user, codes, primary_code, assignments, stats } = entry;
  const groupedByPlatform = useMemo(() => {
    const m = new Map();
    (assignments || []).forEach((a) => {
      const key = a.package?.type || a.platform || "—";
      const arr = m.get(key) || [];
      arr.push(a);
      m.set(key, arr);
    });
    return Array.from(m.entries());
  }, [assignments]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        p: { xs: 2, md: 3 },
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 8px 32px rgba(0,0,0,0.35)"
            : "0 12px 40px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: "primary.main",
              fontWeight: 700,
            }}
          >
            {initialsFor(user)}
          </Avatar>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>
              {fullNameFor(user)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ wordBreak: "break-all" }}
            >
              {user.email}
              {user.paypal_email ? (
                <Box component="span" sx={{ ml: 1, opacity: 0.7 }}>
                  · PayPal: {user.paypal_email}
                </Box>
              ) : null}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
              {codes.length === 0 ? (
                <Chip size="small" label="no code" color="default" variant="outlined" />
              ) : (
                codes.map((c) => (
                  <Chip
                    key={c}
                    size="small"
                    label={c}
                    color="primary"
                    sx={{
                      fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                      fontWeight: 700,
                    }}
                  />
                ))
              )}
              {user.enabled_earning ? (
                <Chip
                  size="small"
                  label="enabled earning"
                  color="success"
                  variant="outlined"
                />
              ) : null}
              {user.accept_terms_earn_money ? (
                <Chip
                  size="small"
                  label="accepted terms"
                  color="success"
                  variant="outlined"
                />
              ) : null}
            </Stack>
          </Stack>
        </Stack>

        <Stack direction={{ xs: "row", md: "row" }} spacing={3} alignItems="center">
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}
            >
              Earned
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              €{Number(stats?.total_earned || 0).toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats?.conversions_count || 0} conversions
            </Typography>
          </Box>
          <MDButton color="info" startIcon={<EditIcon />} onClick={onEdit}>
            Manage packages
          </MDButton>
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ fontWeight: 700 }}
        >
          Assigned discounted packages ({assignments?.length || 0})
        </Typography>
        {assignments?.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No discounted packages tied to {primary_code || "this code"} yet.
            Click <strong>Manage packages</strong> to assign some.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {groupedByPlatform.map(([platform, items]) => (
              <Box key={platform}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Chip
                    size="small"
                    label={PLATFORM_LABEL[platform] || platform}
                    color={platformChipColor(platform)}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {items.length} {items.length === 1 ? "package" : "packages"}
                  </Typography>
                </Stack>
                <Stack
                  spacing={0.5}
                  sx={{
                    pl: 1,
                    borderLeft: "2px solid",
                    borderColor: "divider",
                    py: 0.5,
                  }}
                >
                  {items.map((a) => {
                    const pkg = a.package;
                    if (!pkg) {
                      return (
                        <Stack
                          key={a.id}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Typography variant="body2" color="error">
                            (package missing / deleted)
                          </Typography>
                        </Stack>
                      );
                    }
                    return (
                      <Stack
                        key={a.id}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            wordBreak: "break-word",
                          }}
                        >
                          {pkg.name || "(no name)"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontFamily:
                              "ui-monospace, Menlo, Consolas, monospace",
                          }}
                        >
                          {pkg.identifier || pkg.product_id}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`€${pkg.price}`}
                          sx={{ fontWeight: 600 }}
                        />
                        {pkg.deleted ? (
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label="package soft-deleted"
                          />
                        ) : null}
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

/*
  Two-column-ish picker. The catalog is split into platform tabs so
  the admin doesn't have to scroll through every variant; ticking a
  row in any tab adds/removes it from the cumulative `selectedIds`
  set, which is rendered in a sticky summary at the top so the admin
  can see what's about to be saved without leaving the dialog.
*/
function ManageAssignmentsDialog({ entry, catalog, onClose, onSaved }) {
  const platforms = useMemo(() => {
    const set = new Set();
    catalog.forEach((p) => {
      if (p.type) set.add(p.type);
    });
    const order = [
      "ios",
      "android",
      "web",
      "ios_web",
      "android_web",
      "ios_android_web",
    ];
    return order.filter((t) => set.has(t));
  }, [catalog]);

  const [activeTab, setActiveTab] = useState(platforms[0] || "ios");
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => {
    const set = new Set();
    (entry.assignments || []).forEach((a) => {
      if (a?.package?.id) set.add(String(a.package.id));
    });
    return set;
  });
  const [code, setCode] = useState(entry.primary_code || "");
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredCatalog = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return catalog
      .filter((p) => p.type === activeTab)
      .filter((p) => {
        if (!needle) return true;
        const hay =
          `${p.name || ""} ${p.identifier || ""} ${p.product_id || ""} ${p.package_title || ""}`.toLowerCase();
        return hay.includes(needle);
      });
  }, [catalog, activeTab, query]);

  const selectedPackages = useMemo(() => {
    const byId = new Map(catalog.map((p) => [String(p.id), p]));
    return Array.from(selectedIds).map((id) => byId.get(id)).filter(Boolean);
  }, [selectedIds, catalog]);

  const handleSave = async () => {
    setSaving(true);
    const result = await AdminUpdateDiscountUserAssignmentsCall(entry.user.id, {
      packageIds: Array.from(selectedIds),
      code: code && code !== entry.primary_code ? code : undefined,
    });
    setSaving(false);
    if (result.ok) {
      onSaved?.();
    } else {
      openAppAlert({
        title: "Could not save",
        message: result.message || "Please try again.",
        color: "error",
      });
    }
  };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Manage discounted packages
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fullNameFor(entry.user)} · {entry.user.email}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="flex-start"
          >
            <TextField
              size="small"
              label="Discount code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 12))}
              helperText="Applies to every package below. Leave blank to keep the existing code."
              sx={{ minWidth: 260 }}
              InputProps={{
                sx: {
                  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                  fontWeight: 700,
                },
              }}
            />
            <Paper
              variant="outlined"
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700 }}
              >
                Selected packages ({selectedPackages.length})
              </Typography>
              {selectedPackages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Pick at least one package below — once a buyer enters{" "}
                  <strong>{code || entry.primary_code || "this code"}</strong>{" "}
                  these are the packages that replace the defaults.
                </Typography>
              ) : (
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", mt: 0.5 }}>
                  {selectedPackages.map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={`${p.name || p.product_id} · ${PLATFORM_LABEL[p.type] || p.type}`}
                      onDelete={() => toggle(String(p.id))}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          </Stack>

          <Box>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: "1px solid", borderColor: "divider", minHeight: 36 }}
            >
              {platforms.map((p) => (
                <Tab
                  key={p}
                  value={p}
                  label={PLATFORM_LABEL[p] || p}
                  sx={{ textTransform: "none", fontWeight: 600, minHeight: 36 }}
                />
              ))}
            </Tabs>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by name, identifier or product_id"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mt: 1.5 }}
            />
            <List
              sx={{
                mt: 1,
                maxHeight: 380,
                overflowY: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                py: 0,
              }}
            >
              {filteredCatalog.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No packages match this search.
                  </Typography>
                </Box>
              ) : (
                filteredCatalog.map((p) => {
                  const id = String(p.id);
                  const selected = selectedIds.has(id);
                  return (
                    <ListItemButton
                      key={id}
                      onClick={() => toggle(id)}
                      sx={{ pl: 1 }}
                    >
                      <Checkbox edge="start" checked={selected} tabIndex={-1} />
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {p.name || "(no name)"}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`€${p.price}`}
                              sx={{ fontWeight: 600 }}
                            />
                            {p.package_title ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={p.package_title}
                              />
                            ) : null}
                          </Stack>
                        }
                        secondary={packageLabel(p)}
                        secondaryTypographyProps={{
                          sx: {
                            fontFamily:
                              "ui-monospace, Menlo, Consolas, monospace",
                            fontSize: 12,
                            wordBreak: "break-all",
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })
              )}
            </List>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </MDButton>
        <MDButton
          variant="contained"
          color="info"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save assignments"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
